import { useState, useCallback, useEffect } from 'react';
import { Card, Title } from '@nimbus-ds/components';
import { useModal, useToastStatus } from 'commons/hooks';
import { useAddressingConfigurationRulesManagement } from 'domains/Shipping/DeliveryMethods/hooks';
import { useLocations } from 'domains/Shipping/Locations/hooks';
import MultiCdEnableModal from 'domains/Shipping/Locations/pages/LocationListPage/components/MultiCdEnableModal';
import {
  trackingShippingEditAddressingRules,
  trackingShippingLocationsPriorityModal,
} from 'domains/Shipping/tracking';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import LocationsPriorityModal from './LocationsPriorityModal';
import RulesEditBodyAndFooter from './RulesEditBodyAndFooter/RulesEditBodyAndFooter';
import RulesListBodyAndFooter from './RulesListBodyAndFooter';
import RulesListDisabledBodyAndFooter from './RulesListBodyAndFooter/RulesListDisabledBodyAndFooter';
import AddressingConfigurationInfoCardSkeleton from './Skeleton';

function AddressingConfigurationInfoCard(): JSX.Element {
  const t = useTranslationShipping('deliveryMethods.addressingConfigurationV2');
  const [isEditing, setIsEditing] = useState(false);

  const {
    rules,
    fetchRules,
    saveStatus,
    cleanStatus,
    hasError,
    isLoadingRules,
  } = useAddressingConfigurationRulesManagement();
  const { locations } = useLocations();
  const [showMulticdModal, openMulticdModal, closeMulticdModal] = useModal();
  const [
    showLocationsPriorityModal,
    openLocationsPriorityModal,
    closeLocationsPriorityModal,
  ] = useModal();

  const hasOnlyOneLocation = locations.length === 1;
  const hasMultipleLocations = locations.length > 1;

  const handleStopEditing = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleStartEditing = useCallback(() => {
    trackingShippingEditAddressingRules(rules, 'edit');
    setIsEditing(true);
  }, [rules]);

  const handleOpenLocationsPriorityModal = useCallback(() => {
    trackingShippingLocationsPriorityModal(rules, 'open');
    openLocationsPriorityModal();
  }, [rules, openLocationsPriorityModal]);

  const handleCloseLocationsPriorityModal = useCallback(
    (action: 'save' | 'close') => {
      trackingShippingLocationsPriorityModal(rules, action);
      closeLocationsPriorityModal();
    },
    [rules, closeLocationsPriorityModal],
  );

  useToastStatus({
    error: t('info.toast.error'),
    success: t('info.toast.success'),
    status: saveStatus,
    onSuccess: cleanStatus,
  });

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  return (
    <Card>
      <Card.Header>
        <Title as="h4">{t('info.title')}</Title>
      </Card.Header>

      {isEditing && (
        <RulesEditBodyAndFooter
          handleStopEditing={handleStopEditing}
          openLocationsPriorityModal={handleOpenLocationsPriorityModal}
        />
      )}

      {hasOnlyOneLocation && !isLoadingRules && (
        <RulesListDisabledBodyAndFooter
          rules={rules}
          isError={hasError}
          fetchRules={fetchRules}
          openMulticdModal={openMulticdModal}
        />
      )}

      {hasMultipleLocations && !isEditing && !isLoadingRules && (
        <RulesListBodyAndFooter
          rules={rules}
          isError={hasError}
          handleEdit={handleStartEditing}
          fetchRules={fetchRules}
        />
      )}

      {isLoadingRules && <RulesListBodyAndFooter.Skeleton />}

      {!hasError && showMulticdModal && (
        <MultiCdEnableModal onClose={closeMulticdModal} />
      )}
      {!hasError && showLocationsPriorityModal && (
        <LocationsPriorityModal onClose={handleCloseLocationsPriorityModal} />
      )}
    </Card>
  );
}

AddressingConfigurationInfoCard.Skeleton =
  AddressingConfigurationInfoCardSkeleton;
export default AddressingConfigurationInfoCard;
